import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_EXTRACTION_JOBS } from '@ctw/shared/utils/query-keys';

export interface ExtractionFieldValue {
  display: string;
  value: string;
  citationIds: Array<number>;
}

export interface ExtractionGroup {
  display: string;
  values: Record<string, ExtractionFieldValue>;
}

export interface ExtractionCitation {
  id: number;
  resourceType: string;
  resourceId: string;
}

export interface ExtractionJobAttributes {
  status: string;
  createdAt: string;
  updatedAt: string;
  groups: Record<string, ExtractionGroup>;
  citations: Array<ExtractionCitation>;
}

interface ResourceIdentifier {
  type: string;
  id: string;
}

export interface ExtractionJobConfig {
  id: string;
  name: string | null;
}

export interface ExtractionJob {
  type: string;
  id: string;
  attributes: ExtractionJobAttributes;
  relationships?: {
    patient?: {
      data?: ResourceIdentifier;
    };
    config?: {
      data?: ResourceIdentifier | null;
    };
  };
  // Resolved from the response's `included` configs by resolveExtractionJobs;
  // not part of the raw job resource returned by the API.
  config?: ExtractionJobConfig;
}

interface IncludedConfig {
  type: 'configs';
  id: string;
  attributes: { name: string | null };
}

// JSON:API compound document from the summarization service. The service
// always includes each job's config resource (no ?include= negotiation).
interface ExtractionJobsResponse {
  data: Array<ExtractionJob>;
  included?: Array<IncludedConfig>;
}

export function resolveExtractionJobs(response: ExtractionJobsResponse): Array<ExtractionJob> {
  const configsById = new Map<string, ExtractionJobConfig>(
    (response.included ?? [])
      .filter((resource) => resource.type === 'configs')
      .map((resource) => [resource.id, { id: resource.id, name: resource.attributes.name }]),
  );
  return (response.data ?? []).map((job) => {
    const configId = job.relationships?.config?.data?.id;
    const config = configId === undefined ? undefined : configsById.get(configId);
    return config ? { ...job, config } : job;
  });
}

export function useExtractionJobs(upid: string | undefined) {
  return usePatientQuery<Array<ExtractionJob>>({
    queryId: QUERY_KEY_AI_EXTRACTION_JOBS,
    queryKey: [upid],
    queryFn: async ({ requestContext, ctwFetch }) => {
      if (!upid) {
        return [];
      }

      const endpoint = `${getAiServiceBaseUrl({
        env: requestContext.env,
      })}/extraction-jobs?upid=${encodeURIComponent(upid)}`;

      const response = await ctwFetch<ExtractionJobsResponse>(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${requestContext.authToken}`,
          'Zus-Account': requestContext.builderId,
        },
      });

      return resolveExtractionJobs(response.data);
    },
  });
}
